/**
 * @fileoverview Paginated A4 letter preview
 *
 * Purpose: Render a letter preview as a stack of independent A4 page
 * cards, each carrying its own header/footer/side-band rendering based
 * on the configured page rules.
 *
 * MOR-521 — preview/print parity: this component is a THIN RENDERER over
 * the export engine. It does NOT reimplement pagination. Instead it:
 *
 *   1. Renders the body once into a hidden measurement DOM that carries the
 *      EXACT same `.letter-body` typography CSS the PDF print window uses
 *      (injected via `buildLetterBodyCss`, scoped to this component).
 *   2. Collects break candidates / mandatory breaks / heights using the
 *      SAME collectors the export pipeline runs (`collectBreakCandidates`,
 *      `collectTableSafeBreakCandidates`, `collectMandatoryBreaks`).
 *   3. Feeds a `PaginationMeasured` into the export's own
 *      `computePaginationLayout(parts, measured)` and renders each returned
 *      `PaginatedPage` using its geometry verbatim.
 *
 * Because the CSS, the measurements, and the pagination math are all shared
 * with `exportHtmlDocument.ts`, the page breaks shown here match the
 * downloaded PDF line-for-line. The header/footer/side bands + page-rule
 * badges are still rendered as React chrome (so we keep DOMPurify
 * sanitisation, `<img>` onError handling, and the badge overlays), but every
 * width/height/offset comes from the `PaginatedPage` object.
 *
 * Layout per page:
 *
 *   ┌────────────────────────────────────────────────┐  ← A4 page card
 *   │ side │  HEADER (headerSlotHeightPx)      │side│
 *   │      ├────────────────────────────────────┤    │
 *   │ band │                                    │band│
 *   │      │   BODY CLIP (height = contentH)    │    │
 *   │      │     translated by yOffsetPx        │    │
 *   │      ├────────────────────────────────────┤    │
 *   │      │  FOOTER (footerSlotHeightPx)       │    │
 *   └────────────────────────────────────────────────┘
 *
 * Used by:
 *   - `src/routes/_app/_auth/dashboard/_layout.jobs.$jobId.day-one.tsx`
 *   - `src/routes/_app/_auth/dashboard/_layout.settings.docs.tsx`
 */

import DOMPurify from "dompurify";
import {
  Fragment,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  NO_PAGE_FIT,
  applyPageFitToPadding,
  fitZoneHtml,
  footerSlotInsets,
  zonePageFit,
} from "@/utils/branding/pageFit";
import {
  pickVariantForPage,
  sideCropGeometry,
  sideCropIsActive,
  EMPTY_SIDE_CROP,
  type DocumentPadding,
  type PageRule,
  type SideCrop,
  type VariantFragments,
} from "@/utils/branding/resolve-letterhead";
import {
  A4_PAGE_HEIGHT_PX,
  A4_PAGE_WIDTH_PX,
  buildLetterBodyCss,
  buildWrapSpacerHtml,
  collectBreakCandidates,
  collectMandatoryBreaks,
  collectTableSafeBreakCandidates,
  computePaginationLayout,
  computeTopBlockDims,
  computeWrapSpacers,
  getVariantsForParts,
  normalizeTableWidthsForOutput,
  type LetterParts,
  type PaginationMeasured,
  type TopBlockPx,
  type PaginatedPage,
  distinctBodyGeometries,
  collectBlockTops,
  type BodyMeasurement,
} from "@/utils/export/exportHtmlDocument";
import { normalizeManualListMarkers } from "@/ui/composed/editor/manual-list-markers";

const A4_WIDTH_PX = A4_PAGE_WIDTH_PX;
const A4_HEIGHT_PX = A4_PAGE_HEIGHT_PX;

/**
 * Load an image off-screen and resolve its rendered width when scaled to
 * exactly `A4_HEIGHT_PX` tall. Resolves to 0 on failure (so callers can
 * skip side-band rendering gracefully).
 */
function measureSideWidthAtA4Height(url: string): Promise<number> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (!img.naturalHeight) {
        resolve(0);
        return;
      }
      const ratio = A4_HEIGHT_PX / img.naturalHeight;
      resolve(Math.round(img.naturalWidth * ratio));
    };
    img.onerror = () => resolve(0);
    img.src = url;
  });
}

/**
 * Load a partial-height ("topBlock") side image off-screen and resolve its
 * on-page block dimensions (the CROPPED region's aspect at the configured
 * page-height fraction — same math as the export engine's measurement
 * pass). Resolves to null on failure so callers skip the block gracefully.
 */
function measureTopBlockDims(
  url: string,
  heightPct: number,
  crop: SideCrop,
): Promise<TopBlockPx | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const visibleX = Math.max(0.1, 1 - crop.left - crop.right);
      const visibleY = Math.max(0.1, 1 - crop.top - crop.bottom);
      resolve(
        computeTopBlockDims(
          img.naturalWidth * visibleX,
          img.naturalHeight * visibleY,
          heightPct,
        ),
      );
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/**
 * Render a topBlock side image inside its fixed-size block, honouring the
 * source crop: the image is oversized inside the overflow-hidden block and
 * shifted so only the cropped region shows.
 */
function TopBlockImage({
  src,
  alt,
  crop,
}: {
  src: string;
  alt: string;
  crop: SideCrop;
}) {
  if (!sideCropIsActive(crop)) {
    return (
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "fill",
          display: "block",
        }}
      />
    );
  }
  const geo = sideCropGeometry(crop);
  return (
    <img
      src={src}
      alt={alt}
      style={{
        position: "absolute",
        display: "block",
        width: `${geo.imgWidthPct}%`,
        height: `${geo.imgHeightPct}%`,
        left: `-${geo.offsetLeftPct}%`,
        top: `-${geo.offsetTopPct}%`,
      }}
    />
  );
}

export interface PaginatedLetterPreviewProps {
  /**
   * Structured letter parts — the SAME object built for the PDF/Word export.
   * Drives variant selection, padding, fonts/spacing, and the pagination
   * math (via `computePaginationLayout`). The body actually rendered &
   * measured comes from `bodyHtml` below (which equals `parts.bodyHtml` in
   * non-editing mode but may carry field-placeholder chips while editing).
   */
  parts: LetterParts;

  /**
   * Body HTML to render in the centre column. If `bodyAlreadySanitised`
   * is true, the HTML is injected without an additional DOMPurify pass.
   */
  bodyHtml: string;
  bodyClassName?: string;
  bodyAlreadySanitised?: boolean;
  extraSanitiseAttrs?: string[];

  /** Third-party forms render without letterhead and without pagination. */
  isThirdPartyForm?: boolean;

  /** Resolved document padding (header/body/footer top/bottom/side). */
  docPadding: DocumentPadding;

  /**
   * Optional component used to render a "page rule" badge over each
   * letterhead zone when its page rule is anything other than `"all"`.
   */
  PageRuleBadgeComponent?: ComponentType<{ rule: PageRule }>;

  /** Optional overrides for each individual page card (fonts/colour). */
  pageCardStyle?: CSSProperties;
  /** Optional className applied to each page card. */
  pageCardClassName?: string;
  /**
   * Optional per-page controls (e.g. the "Page N margins" popover), rendered
   * in the page card's top-right corner above all letterhead layers.
   */
  renderPageActions?: (page: PaginatedPage) => ReactNode;
}

export function PaginatedLetterPreview({
  parts,
  bodyHtml,
  bodyClassName = "letter-body prose-letter",
  bodyAlreadySanitised = false,
  extraSanitiseAttrs,
  isThirdPartyForm = false,
  docPadding,
  PageRuleBadgeComponent,
  pageCardStyle,
  pageCardClassName,
  renderPageActions,
}: PaginatedLetterPreviewProps) {
  const showLetterhead = !isThirdPartyForm;

  // Ordered variant list (highest priority first), resolved exactly the way
  // the export engine resolves it so per-page variant selection matches.
  const variants = useMemo<VariantFragments[]>(
    () => (showLetterhead ? getVariantsForParts(parts) : []),
    [showLetterhead, parts],
  );

  // Unique scope so the injected `.letter-body` CSS only applies inside this
  // preview instance (no global leakage; no cross-instance line-height
  // collisions when two previews with different settings mount together).
  const scopeId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const scopeAttr = `morgan-preview-${scopeId}`;
  const scopeSelector = `[data-morgan-preview="${scopeAttr}"] `;

  // The body typography CSS — identical to the PDF print window's
  // `.letter-body` rules (MOR-521 / MOR-565). Scoped to this instance.
  const letterBodyCss = useMemo(
    () =>
      buildLetterBodyCss({
        defaultLineHeight: parts.defaultLineHeight,
        defaultParagraphSpacingBefore: parts.defaultParagraphSpacingBefore,
        defaultParagraphSpacingAfter: parts.defaultParagraphSpacingAfter,
        scope: scopeSelector,
      }),
    [
      parts.defaultLineHeight,
      parts.defaultParagraphSpacingBefore,
      parts.defaultParagraphSpacingAfter,
      scopeSelector,
    ],
  );

  // --- 1. Measure side image widths ---------------------------------
  // Side widths use the WIDEST variant so the body wrap math is correct
  // even on pages where a narrower variant wins. The actual rendered side
  // image still comes from the per-page winning variant.
  //
  // topBlock ("partial height") sides are tracked separately — they reserve
  // no band width; their measured block dimensions instead drive a floated
  // wrap spacer at the top of the body (mirroring the export engine).
  const variantSideUrls = useMemo(() => {
    const left =
      variants.find((v) => v.leftSideImageUrl && v.leftSideFit !== "topBlock")
        ?.leftSideImageUrl ?? null;
    const right =
      variants.find((v) => v.rightSideImageUrl && v.rightSideFit !== "topBlock")
        ?.rightSideImageUrl ?? null;
    const leftTopBlockVariant =
      variants.find(
        (v) => v.leftSideImageUrl && v.leftSideFit === "topBlock",
      ) ?? null;
    const rightTopBlockVariant =
      variants.find(
        (v) => v.rightSideImageUrl && v.rightSideFit === "topBlock",
      ) ?? null;
    return {
      left,
      right,
      leftTopBlockUrl: leftTopBlockVariant?.leftSideImageUrl ?? null,
      leftTopBlockPct: leftTopBlockVariant?.leftSideTopBlockHeightPct ?? 0,
      leftTopBlockCrop: leftTopBlockVariant?.leftSideCrop ?? EMPTY_SIDE_CROP,
      rightTopBlockUrl: rightTopBlockVariant?.rightSideImageUrl ?? null,
      rightTopBlockPct: rightTopBlockVariant?.rightSideTopBlockHeightPct ?? 0,
      rightTopBlockCrop: rightTopBlockVariant?.rightSideCrop ?? EMPTY_SIDE_CROP,
    };
  }, [variants]);

  const [leftWidthPx, setLeftWidthPx] = useState(0);
  const [rightWidthPx, setRightWidthPx] = useState(0);
  const [leftTopBlockDims, setLeftTopBlockDims] = useState<TopBlockPx | null>(
    null,
  );
  const [rightTopBlockDims, setRightTopBlockDims] = useState<TopBlockPx | null>(
    null,
  );

  useEffect(() => {
    const url = variantSideUrls.left;
    if (!showLetterhead || !url) {
      setLeftWidthPx(0);
      return;
    }
    let cancelled = false;
    measureSideWidthAtA4Height(url).then((w) => {
      if (!cancelled) setLeftWidthPx(w);
    });
    return () => {
      cancelled = true;
    };
  }, [showLetterhead, variantSideUrls.left]);

  useEffect(() => {
    const url = variantSideUrls.right;
    if (!showLetterhead || !url) {
      setRightWidthPx(0);
      return;
    }
    let cancelled = false;
    measureSideWidthAtA4Height(url).then((w) => {
      if (!cancelled) setRightWidthPx(w);
    });
    return () => {
      cancelled = true;
    };
  }, [showLetterhead, variantSideUrls.right]);

  useEffect(() => {
    const url = variantSideUrls.leftTopBlockUrl;
    if (!showLetterhead || !url) {
      setLeftTopBlockDims(null);
      return;
    }
    let cancelled = false;
    measureTopBlockDims(
      url,
      variantSideUrls.leftTopBlockPct,
      variantSideUrls.leftTopBlockCrop,
    ).then((dims) => {
      if (!cancelled) setLeftTopBlockDims(dims);
    });
    return () => {
      cancelled = true;
    };
  }, [
    showLetterhead,
    variantSideUrls.leftTopBlockUrl,
    variantSideUrls.leftTopBlockPct,
    variantSideUrls.leftTopBlockCrop,
  ]);

  useEffect(() => {
    const url = variantSideUrls.rightTopBlockUrl;
    if (!showLetterhead || !url) {
      setRightTopBlockDims(null);
      return;
    }
    let cancelled = false;
    measureTopBlockDims(
      url,
      variantSideUrls.rightTopBlockPct,
      variantSideUrls.rightTopBlockCrop,
    ).then((dims) => {
      if (!cancelled) setRightTopBlockDims(dims);
    });
    return () => {
      cancelled = true;
    };
  }, [
    showLetterhead,
    variantSideUrls.rightTopBlockUrl,
    variantSideUrls.rightTopBlockPct,
    variantSideUrls.rightTopBlockCrop,
  ]);

  const bodyAvailableWidthPx = Math.max(
    1,
    A4_WIDTH_PX - leftWidthPx - rightWidthPx,
  );

  // Per-layout margins / page overrides can give pages different body
  // column geometries. Each distinct geometry gets its own hidden
  // measurement body below (mirrors `measurePrintLayout`'s per-flow clones)
  // so page 2 reflows to its real width instead of page 1's.
  const geometries = useMemo(
    () =>
      isThirdPartyForm
        ? []
        : distinctBodyGeometries(parts, {
            leftWidth: leftWidthPx,
            rightWidth: rightWidthPx,
          }),
    [parts, leftWidthPx, rightWidthPx, isThirdPartyForm],
  );
  const multiFlow = geometries.length > 1;
  const [flowMeasurements, setFlowMeasurements] = useState<BodyMeasurement[]>(
    [],
  );
  const measureFlowsRef = useRef<HTMLDivElement | null>(null);

  // --- 2. Pre-sanitise content for measurement + render -------------
  const sanitisedBody = useMemo(() => {
    const raw = bodyAlreadySanitised
      ? bodyHtml
      : DOMPurify.sanitize(bodyHtml, {
          ADD_ATTR: extraSanitiseAttrs,
        });
    // Match the editor's table sizing (every table fills the body width,
    // resized columns become proportions) so the preview agrees with the
    // PDF/Word exports. Then snap manual "(iii)&nbsp;&nbsp;…" list markers in
    // hanging-indent paragraphs into fixed-width boxes so justification can't
    // stretch them out of alignment (MOR-977) — covers templates stored
    // before the editor-side normalisation baked the boxes in.
    return normalizeManualListMarkers(normalizeTableWidthsForOutput(raw));
  }, [bodyHtml, bodyAlreadySanitised, extraSanitiseAttrs]);

  // Pre-sanitise every variant's header + footer HTML. Used for both the
  // off-screen measurement DOM (max across variants -> reserved heights) and
  // the per-page render (per-page winner -> visible HTML).
  const sanitisedVariants = useMemo(() => {
    if (!showLetterhead)
      return [] as Array<{
        header: string;
        footer: string;
        headerPageRule: PageRule;
        footerPageRule: PageRule;
        leftSidePageRule: PageRule;
        rightSidePageRule: PageRule;
        leftSideImageUrl: string | null;
        rightSideImageUrl: string | null;
        headerPageFitTop: number | null;
        footerPageFitBottom: number | null;
      }>;
    return variants.map((v) => ({
      header: v.headerHtml
        ? DOMPurify.sanitize(v.headerHtml, { ADD_DATA_URI_TAGS: ["img"] })
        : "",
      footer: v.footerHtml
        ? DOMPurify.sanitize(v.footerHtml, { ADD_DATA_URI_TAGS: ["img"] })
        : "",
      headerPageRule: v.headerPageRule,
      footerPageRule: v.footerPageRule,
      leftSidePageRule: v.leftSidePageRule,
      rightSidePageRule: v.rightSidePageRule,
      leftSideImageUrl: v.leftSideImageUrl ?? null,
      rightSideImageUrl: v.rightSideImageUrl ?? null,
      headerPageFitTop: v.headerPageFitTop ?? null,
      footerPageFitBottom: v.footerPageFitBottom ?? null,
    }));
  }, [showLetterhead, variants]);

  // --- 3. Hidden measurement DOM for header/footer/body heights -----
  const measureHeaderContainerRef = useRef<HTMLDivElement>(null);
  const measureFooterContainerRef = useRef<HTMLDivElement>(null);
  const measureBodyRef = useRef<HTMLDivElement>(null);

  const [headerHeightPx, setHeaderHeightPx] = useState(0);
  const [footerHeightPx, setFooterHeightPx] = useState(0);
  const [bodyContentHeightPx, setBodyContentHeightPx] = useState(0);
  const [mandatoryBreaks, setMandatoryBreaks] = useState<number[]>([]);
  const [breakCandidates, setBreakCandidates] = useState<number[]>([]);
  const [tableSafeBreakCandidates, setTableSafeBreakCandidates] = useState<
    number[]
  >([]);

  // Mirror the export's `waitForPrintWindowReady`: don't trust the first
  // measurement until webfonts have loaded, otherwise fallback-font metrics
  // produce break offsets that drift from the PDF until a reflow.
  const [fontsReadyTick, setFontsReadyTick] = useState(0);
  useEffect(() => {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts?.ready) return;
    let cancelled = false;
    fonts.ready.then(() => {
      if (!cancelled) setFontsReadyTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // --- 3b. topBlock wrap spacers -------------------------------------
  //
  // Floated spacers modelling a partial-height side block's intrusion into
  // the body flow (computed by the SAME helper the export engine uses).
  // They're prepended to the body HTML for BOTH the measurement DOM and
  // every rendered page copy, so the wrapped text layout — and therefore
  // every break offset — matches the PDF exactly.
  const wrapSpacers = useMemo(() => {
    if (!showLetterhead || (!leftTopBlockDims && !rightTopBlockDims)) {
      return { left: null, right: null };
    }
    return computeWrapSpacers(
      parts,
      {
        headerHeight: headerHeightPx,
        leftTopBlock: leftTopBlockDims,
        rightTopBlock: rightTopBlockDims,
      },
      docPadding,
    );
  }, [
    showLetterhead,
    parts,
    headerHeightPx,
    leftTopBlockDims,
    rightTopBlockDims,
    docPadding,
  ]);

  const bodyHtmlWithSpacers = useMemo(() => {
    const spacerHtml =
      buildWrapSpacerHtml("left", wrapSpacers.left) +
      buildWrapSpacerHtml("right", wrapSpacers.right);
    return spacerHtml ? spacerHtml + sanitisedBody : sanitisedBody;
  }, [wrapSpacers, sanitisedBody]);

  useEffect(() => {
    const container = measureHeaderContainerRef.current;
    if (!container) {
      setHeaderHeightPx(0);
      return;
    }
    const measure = () => {
      const max = (Array.from(container.children) as HTMLElement[]).reduce(
        (best, child) => Math.max(best, child.offsetHeight),
        0,
      );
      setHeaderHeightPx(max);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    for (const child of Array.from(container.children)) {
      ro.observe(child);
    }
    // MOR-769 — re-measure when async letterhead images settle (see the
    // body-measure effect below for the full rationale).
    const cleanups: Array<() => void> = [];
    for (const img of Array.from(container.querySelectorAll("img"))) {
      if (img.complete) continue;
      const onSettled = () => measure();
      img.addEventListener("load", onSettled);
      img.addEventListener("error", onSettled);
      cleanups.push(() => {
        img.removeEventListener("load", onSettled);
        img.removeEventListener("error", onSettled);
      });
    }
    return () => {
      ro.disconnect();
      for (const cleanup of cleanups) cleanup();
    };
  }, [sanitisedVariants, bodyAvailableWidthPx, docPadding, fontsReadyTick]);

  useEffect(() => {
    const container = measureFooterContainerRef.current;
    if (!container) {
      setFooterHeightPx(0);
      return;
    }
    const measure = () => {
      const max = (Array.from(container.children) as HTMLElement[]).reduce(
        (best, child) => Math.max(best, child.offsetHeight),
        0,
      );
      setFooterHeightPx(max);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    for (const child of Array.from(container.children)) {
      ro.observe(child);
    }
    // MOR-769 — re-measure when async letterhead images settle (see the
    // body-measure effect below for the full rationale).
    const cleanups: Array<() => void> = [];
    for (const img of Array.from(container.querySelectorAll("img"))) {
      if (img.complete) continue;
      const onSettled = () => measure();
      img.addEventListener("load", onSettled);
      img.addEventListener("error", onSettled);
      cleanups.push(() => {
        img.removeEventListener("load", onSettled);
        img.removeEventListener("error", onSettled);
      });
    }
    return () => {
      ro.disconnect();
      for (const cleanup of cleanups) cleanup();
    };
  }, [sanitisedVariants, bodyAvailableWidthPx, docPadding, fontsReadyTick]);

  useEffect(() => {
    const el = measureBodyRef.current;
    if (!el) {
      setBodyContentHeightPx(0);
      setMandatoryBreaks([]);
      setBreakCandidates([]);
      setTableSafeBreakCandidates([]);
      return;
    }
    const measure = () => {
      setBodyContentHeightPx(el.offsetHeight);
      // Use the EXACT collectors the export pipeline runs so the break
      // points (and therefore the page boundaries) match the PDF.
      setMandatoryBreaks(collectMandatoryBreaks(el));
      setBreakCandidates(collectBreakCandidates(el));
      setTableSafeBreakCandidates(collectTableSafeBreakCandidates(el));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // MOR-769 — the export path measures only after `waitForImages`
    // (waitForPrintWindowReady), but the preview used to measure while any
    // remote <img> (typically the inserted signature, which has no intrinsic
    // dimensions — only max-height:60px) was still 0px tall. Every break
    // offset below it was then stale by the image height, so the rendered
    // page clips sliced mid-element ("Simon Cathro" cut in half at the page
    // boundary). The ResizeObserver above only fires when the CONTAINER's
    // height changes, which can miss late image loads inside fixed-height
    // structures — so re-measure explicitly when each pending image settles.
    const cleanups: Array<() => void> = [];
    for (const img of Array.from(el.querySelectorAll("img"))) {
      if (img.complete) continue;
      const onSettled = () => measure();
      img.addEventListener("load", onSettled);
      img.addEventListener("error", onSettled);
      cleanups.push(() => {
        img.removeEventListener("load", onSettled);
        img.removeEventListener("error", onSettled);
      });
    }
    return () => {
      ro.disconnect();
      for (const cleanup of cleanups) cleanup();
    };
  }, [
    bodyHtmlWithSpacers,
    bodyAvailableWidthPx,
    docPadding,
    pageCardStyle,
    fontsReadyTick,
  ]);

  // --- 3b. Per-flow body measurement (multi-geometry documents only) ----
  useEffect(() => {
    const root = measureFlowsRef.current;
    if (!multiFlow || !root) {
      setFlowMeasurements([]);
      return;
    }
    const measure = () => {
      const slots = Array.from(
        root.querySelectorAll<HTMLElement>("[data-preview-measure-flow]"),
      );
      const next: BodyMeasurement[] = geometries.map((g, i) => {
        const el = slots[i];
        return {
          ...g,
          bodyHeight: el?.offsetHeight ?? 0,
          breakCandidates: el ? collectBreakCandidates(el) : [],
          tableSafeBreakCandidates: el
            ? collectTableSafeBreakCandidates(el)
            : [],
          mandatoryBreaks: el ? collectMandatoryBreaks(el) : [],
          tableSpans: [],
          blockTops: el ? collectBlockTops(el) : [],
        };
      });
      setFlowMeasurements(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    const cleanups: Array<() => void> = [];
    for (const img of Array.from(root.querySelectorAll("img"))) {
      if (img.complete) continue;
      const onSettled = () => measure();
      img.addEventListener("load", onSettled);
      img.addEventListener("error", onSettled);
      cleanups.push(() => {
        img.removeEventListener("load", onSettled);
        img.removeEventListener("error", onSettled);
      });
    }
    return () => {
      ro.disconnect();
      for (const cleanup of cleanups) cleanup();
    };
  }, [
    multiFlow,
    geometries,
    bodyHtmlWithSpacers,
    docPadding,
    pageCardStyle,
    fontsReadyTick,
  ]);

  // --- 4. Pagination via the export engine --------------------------
  //
  // Feed the in-DOM measurements into the SAME pure function that drives the
  // PDF (`computePaginationLayout`). The returned pages carry per-page
  // reserved heights, side reservations, and body slice offsets — we render
  // them verbatim so the preview can't diverge from the print output.
  const pages = useMemo(() => {
    if (isThirdPartyForm) {
      // Third-party forms aren't paginated by the export either — render a
      // single full-height card with no letterhead reservation.
      return [
        {
          pageIndex: 1,
          topReservedPx: 0,
          headerSlotHeightPx: 0,
          bottomReservedPx: 0,
          footerSlotHeightPx: 0,
          leftReservedPx: 0,
          rightReservedPx: 0,
          bodyLeftPx: 0,
          bodyWidthPx: A4_WIDTH_PX,
          yOffsetPx: 0,
          contentHeightPx: A4_HEIGHT_PX,
          headerHtml: "",
          footerHtml: "",
          pageNumberHtml: "",
          showsPageNumber: false,
          leftSideHtml: "",
          rightSideHtml: "",
          padding: docPadding,
          bodyMeasurementIndex: -1,
        } satisfies PaginatedPage,
      ];
    }
    const measured: PaginationMeasured = {
      headerHeight: headerHeightPx,
      footerHeight: footerHeightPx,
      bodyHeight: bodyContentHeightPx,
      leftWidth: leftWidthPx,
      rightWidth: rightWidthPx,
      leftTopBlock: leftTopBlockDims,
      rightTopBlock: rightTopBlockDims,
      breakCandidates,
      tableSafeBreakCandidates,
      mandatoryBreaks,
      ...(multiFlow && flowMeasurements.length === geometries.length
        ? { bodyByGeometry: flowMeasurements }
        : {}),
    };
    return computePaginationLayout(parts, measured);
  }, [
    isThirdPartyForm,
    docPadding,
    parts,
    multiFlow,
    flowMeasurements,
    geometries,
    headerHeightPx,
    footerHeightPx,
    bodyContentHeightPx,
    leftWidthPx,
    rightWidthPx,
    leftTopBlockDims,
    rightTopBlockDims,
    breakCandidates,
    tableSafeBreakCandidates,
    mandatoryBreaks,
  ]);

  // --- 5. Render -----------------------------------------------------
  const measurementContainerStyle: CSSProperties = {
    position: "absolute",
    left: -99999,
    top: 0,
    pointerEvents: "none",
    visibility: "hidden",
    width: bodyAvailableWidthPx,
    ...pageCardStyle,
  };

  return (
    <div
      data-morgan-preview={scopeAttr}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 24,
      }}
    >
      {/* Scoped body typography — identical to the PDF print CSS. */}
      <style
        // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- CSS built by buildLetterBodyCss from numeric typography settings and a useId scope, no external input
        dangerouslySetInnerHTML={{ __html: letterBodyCss }}
      />

      {/* ===== Hidden measurement DOM (off-screen) ===== */}
      <div style={measurementContainerStyle} aria-hidden="true">
        {showLetterhead && (
          <div ref={measureHeaderContainerRef}>
            {sanitisedVariants.map((v, idx) => {
              if (!v.header) return null;
              const slot = applyPageFitToPadding(docPadding, {
                headerTop: v.headerPageFitTop,
                footerBottom: null,
              });
              return (
                <div
                  key={`measure-header-${idx}`}
                  style={{
                    padding: `${slot.headerTop}px ${slot.headerRight}px 0 ${slot.headerLeft}px`,
                  }}
                  // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- v.header is DOMPurify.sanitize'd in the sanitisedVariants memo above; fitZoneHtml only adds a numeric-only wrapper
                  dangerouslySetInnerHTML={{
                    __html: fitZoneHtml(v.header, v.headerPageFitTop),
                  }}
                />
              );
            })}
          </div>
        )}
        {showLetterhead && (
          <div ref={measureFooterContainerRef}>
            {sanitisedVariants.map((v, idx) => {
              if (!v.footer) return null;
              const slot = applyPageFitToPadding(docPadding, {
                headerTop: null,
                footerBottom: v.footerPageFitBottom,
              });
              return (
                <div
                  key={`measure-footer-${idx}`}
                  style={{
                    padding: `0 ${slot.footerRight}px ${slot.footerBottom}px ${slot.footerLeft}px`,
                  }}
                  // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- v.footer is DOMPurify.sanitize'd in the sanitisedVariants memo above; fitZoneHtml only adds a numeric-only wrapper
                  dangerouslySetInnerHTML={{
                    __html: fitZoneHtml(v.footer, v.footerPageFitBottom),
                  }}
                />
              );
            })}
          </div>
        )}
        <div
          ref={measureBodyRef}
          className={bodyClassName}
          style={{
            padding: `${docPadding.bodyTop}px ${docPadding.bodyRight}px ${docPadding.bodyTop}px ${docPadding.bodyLeft}px`,
          }}
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- body is DOMPurify.sanitize'd in the sanitisedBody memo (or upstream by the sole bodyAlreadySanitised caller, settings.docs.tsx); spacers are numeric-only markup
          dangerouslySetInnerHTML={{ __html: bodyHtmlWithSpacers }}
        />
        {multiFlow && (
          <div ref={measureFlowsRef}>
            {geometries.map((g, i) => (
              <div key={`flow-${i}`} style={{ width: g.columnWidthPx }}>
                <div
                  data-preview-measure-flow={i}
                  className={bodyClassName}
                  style={{
                    width: g.columnWidthPx,
                    boxSizing: "border-box",
                    padding: `${docPadding.bodyTop}px ${g.bodyRightPaddingPx}px ${docPadding.bodyTop}px ${g.bodyLeftPaddingPx}px`,
                  }}
                  // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- body is DOMPurify.sanitize'd in the sanitisedBody memo (or upstream by the sole bodyAlreadySanitised caller, settings.docs.tsx)
                  dangerouslySetInnerHTML={{ __html: bodyHtmlWithSpacers }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== Visible: N A4 page cards ===== */}
      {pages.map((page, idx) => {
        const pageIndex = page.pageIndex;

        // Per-zone variant selection — same call the engine makes, so the
        // visible header/footer/side matches what the engine reserved.
        const headerVariant = showLetterhead
          ? pickVariantForPage(variants, "header", pageIndex)
          : null;
        const footerVariant = showLetterhead
          ? pickVariantForPage(variants, "footer", pageIndex)
          : null;
        const leftVariant = showLetterhead
          ? pickVariantForPage(variants, "leftSide", pageIndex)
          : null;
        const rightVariant = showLetterhead
          ? pickVariantForPage(variants, "rightSide", pageIndex)
          : null;

        const winnerIdx = (target: VariantFragments | null) =>
          target ? variants.indexOf(target) : -1;
        const headerIdx = winnerIdx(headerVariant);
        const footerIdx = winnerIdx(footerVariant);
        const pageHeaderHtml =
          headerIdx >= 0 ? (sanitisedVariants[headerIdx]?.header ?? "") : "";
        const pageFooterHtml =
          footerIdx >= 0 ? (sanitisedVariants[footerIdx]?.footer ?? "") : "";

        const showHeader = !!pageHeaderHtml;
        const showFooter = !!pageFooterHtml;
        const showLeftSide =
          !!leftVariant &&
          page.leftReservedPx > 0 &&
          !!leftVariant.leftSideImageUrl;
        const showRightSide =
          !!rightVariant &&
          page.rightReservedPx > 0 &&
          !!rightVariant.rightSideImageUrl;

        // All geometry comes from the engine's PaginatedPage — never
        // recomputed here, so preview == PDF.
        const leftReservedPx = page.leftReservedPx;
        const rightReservedPx = page.rightReservedPx;
        const topReservedPx = page.topReservedPx;
        const centreWidthPx = Math.max(
          1,
          A4_WIDTH_PX - leftReservedPx - rightReservedPx,
        );
        // Body column geometry is FIXED across pages (MOR-911): the body is
        // measured once at `bodyAvailableWidthPx`, so a page that paints no
        // side band must still render its slice at that same width — else
        // the text reflows wider than it was measured and the slice offsets
        // cut lines in half / land manual page breaks on the wrong content.
        const bodyLeftPx = page.bodyLeftPx;
        const bodyWidthPx = page.bodyWidthPx;
        // Header insets around topBlock art (mirrors the export renderer)
        // so a full-width header image never paints underneath the block.
        const headerLeftInsetPx = Math.max(
          leftReservedPx,
          page.leftTopBlock?.widthPx ?? 0,
        );
        const headerRightInsetPx = Math.max(
          rightReservedPx,
          page.rightTopBlock?.widthPx ?? 0,
        );
        const headerWidthPx = Math.max(
          1,
          A4_WIDTH_PX - headerLeftInsetPx - headerRightInsetPx,
        );
        const yOffsetPx = page.yOffsetPx;
        const pageContentHeightPx = page.contentHeightPx;
        // Page-fit header/footer slots ignore the zone margins and lay out
        // at page width (mirrors the export's slot builders).
        const pageFit = showLetterhead
          ? zonePageFit(headerVariant, footerVariant)
          : NO_PAGE_FIT;
        const slotPadding = applyPageFitToPadding(page.padding, pageFit);
        const footerInsets = footerSlotInsets(
          slotPadding,
          pageFit.footerBottom,
          page.showsPageNumber,
        );

        return (
          <Fragment key={`page-${idx}`}>
            {idx > 0 && (
              <div
                style={{
                  width: A4_WIDTH_PX,
                  maxWidth: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  color: "rgba(0, 0, 0, 0.4)",
                  fontSize: 11,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: 0,
                    borderTop: "1px dashed rgba(0, 0, 0, 0.2)",
                  }}
                />
                <span style={{ whiteSpace: "nowrap" }}>Page {pageIndex}</span>
                <div
                  style={{
                    flex: 1,
                    height: 0,
                    borderTop: "1px dashed rgba(0, 0, 0, 0.2)",
                  }}
                />
              </div>
            )}
            <div
              className={pageCardClassName}
              data-page-index={pageIndex}
              style={{
                position: "relative",
                width: A4_WIDTH_PX,
                height: A4_HEIGHT_PX,
                maxWidth: "100%",
                backgroundColor: "white",
                boxSizing: "border-box",
                overflow: "hidden",
                ...pageCardStyle,
              }}
            >
              {renderPageActions && (
                <div
                  className="print:hidden"
                  // Top-left: the page-rule badges live top-right.
                  style={{ position: "absolute", top: 8, left: 8, zIndex: 20 }}
                  data-testid={`page-actions-${pageIndex}`}
                >
                  {renderPageActions(page as PaginatedPage)}
                </div>
              )}
              {/* Side bands — full-height overlays anchored to page edges. */}
              {showLeftSide && leftVariant && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: leftReservedPx,
                    height: A4_HEIGHT_PX,
                    pointerEvents: "none",
                    lineHeight: 0,
                    fontSize: 0,
                  }}
                >
                  <img
                    src={leftVariant.leftSideImageUrl ?? ""}
                    alt="Left side letterhead"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "fill",
                      display: "block",
                    }}
                  />
                  {pageIndex === 1 &&
                    PageRuleBadgeComponent &&
                    leftVariant.leftSidePageRule !== "all" && (
                      <PageRuleBadgeComponent
                        rule={leftVariant.leftSidePageRule}
                      />
                    )}
                </div>
              )}
              {showRightSide && rightVariant && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: rightReservedPx,
                    height: A4_HEIGHT_PX,
                    pointerEvents: "none",
                    lineHeight: 0,
                    fontSize: 0,
                  }}
                >
                  <img
                    src={rightVariant.rightSideImageUrl ?? ""}
                    alt="Right side letterhead"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "fill",
                      display: "block",
                    }}
                  />
                  {pageIndex === 1 &&
                    PageRuleBadgeComponent &&
                    rightVariant.rightSidePageRule !== "all" && (
                      <PageRuleBadgeComponent
                        rule={rightVariant.rightSidePageRule}
                      />
                    )}
                </div>
              )}

              {/* Partial-height ("topBlock") sides — top-corner blocks at
                  natural aspect; body text wraps via the injected spacer. */}
              {page.leftTopBlock && leftVariant?.leftSideImageUrl && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: page.leftTopBlock.widthPx,
                    height: page.leftTopBlock.heightPx,
                    overflow: "hidden",
                    pointerEvents: "none",
                    lineHeight: 0,
                    fontSize: 0,
                  }}
                >
                  <TopBlockImage
                    src={leftVariant.leftSideImageUrl}
                    alt="Left side letterhead"
                    crop={leftVariant.leftSideCrop}
                  />
                  {pageIndex === 1 && PageRuleBadgeComponent && (
                    <PageRuleBadgeComponent rule="firstOnly" />
                  )}
                </div>
              )}
              {page.rightTopBlock && rightVariant?.rightSideImageUrl && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: page.rightTopBlock.widthPx,
                    height: page.rightTopBlock.heightPx,
                    overflow: "hidden",
                    pointerEvents: "none",
                    lineHeight: 0,
                    fontSize: 0,
                  }}
                >
                  <TopBlockImage
                    src={rightVariant.rightSideImageUrl}
                    alt="Right side letterhead"
                    crop={rightVariant.rightSideCrop}
                  />
                  {pageIndex === 1 && PageRuleBadgeComponent && (
                    <PageRuleBadgeComponent rule="firstOnly" />
                  )}
                </div>
              )}

              {/* Header — anchored to the page top inside the centre column. */}
              {showHeader && headerVariant && (
                <div
                  data-preview-slot="header"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: headerLeftInsetPx,
                    width: headerWidthPx,
                    // The slot's own height, not `topReservedPx`: a body
                    // top below the uniform value lifts the body clip but
                    // must not crop the letterhead (MOR-1534).
                    height: page.headerSlotHeightPx,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: `${slotPadding.headerTop}px ${slotPadding.headerRight}px 0 ${slotPadding.headerLeft}px`,
                    }}
                    // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- pageHeaderHtml comes from the sanitisedVariants memo (DOMPurify.sanitize); fitZoneHtml only adds a numeric-only wrapper
                    dangerouslySetInnerHTML={{
                      __html: fitZoneHtml(
                        pageHeaderHtml,
                        pageFit.headerTop,
                        headerLeftInsetPx,
                      ),
                    }}
                  />
                  {pageIndex === 1 &&
                    PageRuleBadgeComponent &&
                    headerVariant.headerPageRule !== "all" && (
                      <PageRuleBadgeComponent
                        rule={headerVariant.headerPageRule}
                      />
                    )}
                </div>
              )}

              {/* Body clip — shows this page's slice of the rendered body. */}
              <div
                data-preview-slot="body-clip"
                style={{
                  position: "absolute",
                  top: topReservedPx,
                  left: bodyLeftPx,
                  width: bodyWidthPx,
                  height: pageContentHeightPx,
                  overflow: "hidden",
                }}
              >
                <div
                  className={bodyClassName}
                  style={{
                    position: "absolute",
                    top: -yOffsetPx,
                    left: 0,
                    width: bodyWidthPx,
                    // Top/bottom use the uniform bodyTop the flow was
                    // measured at (see the measurement slots above); a page's
                    // own bodyTop override is folded into `topReservedPx` by
                    // the engine so break offsets stay aligned.
                    padding: `${docPadding.bodyTop}px ${page.padding.bodyRight}px ${docPadding.bodyTop}px ${page.padding.bodyLeft}px`,
                    boxSizing: "border-box",
                  }}
                  // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- body is DOMPurify.sanitize'd in the sanitisedBody memo (or upstream by the sole bodyAlreadySanitised caller, settings.docs.tsx)
                  dangerouslySetInnerHTML={{ __html: bodyHtmlWithSpacers }}
                />
              </div>

              {/* Footer — anchored to the page bottom inside the centre column. */}
              {showFooter && footerVariant && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: leftReservedPx,
                    width: centreWidthPx,
                    // Excludes the body-bottom gap, which sits between the
                    // body and the footer — not under the footer.
                    height: page.footerSlotHeightPx,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: `${footerInsets.topPx}px ${slotPadding.footerRight}px ${footerInsets.bottomPx}px ${slotPadding.footerLeft}px`,
                    }}
                    // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- pageFooterHtml comes from the sanitisedVariants memo (DOMPurify.sanitize); fitZoneHtml only adds a numeric-only wrapper
                    dangerouslySetInnerHTML={{
                      __html: fitZoneHtml(
                        pageFooterHtml,
                        pageFit.footerBottom,
                        leftReservedPx,
                      ),
                    }}
                  />
                  {pageIndex === 1 &&
                    PageRuleBadgeComponent &&
                    footerVariant.footerPageRule !== "all" && (
                      <PageRuleBadgeComponent
                        rule={footerVariant.footerPageRule}
                      />
                    )}
                </div>
              )}

              {/* Page number — its own bottom-anchored line, sitting below
                  the footer letterhead (which the padding above lifts clear
                  of it). Rendered from the engine's per-page HTML so the
                  format/alignment/inset match the PDF exactly. */}
              {page.showsPageNumber && (
                <div
                  // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- pageNumberHtml is built by buildPageNumberHtml from typed numbering config (enum format, numeric insets)
                  dangerouslySetInnerHTML={{ __html: page.pageNumberHtml }}
                />
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
